import { Component, inject, OnInit } from '@angular/core';
import { API, FileLink } from 'src/app/services/api/api';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { ExportModalOptions } from './sb-export-modal.model';
import { SbDateTime } from 'src/app/services/api/api-date-time';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from 'src/app/services/error.service';
import { MsOptions } from 'src/app/services/global/global-interfaces';
import { StringTransformationService } from '../../../transformation-helper/string-transformation.service';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-comp-export-modal',
  templateUrl: './sb-export-modal.component.html',
  styleUrls: ['./sb-export-modal.component.scss'],
  standalone: false,
})
export class SbExportModalComponent implements OnInit, ModalNavigationComponent {
  private readonly stringTransformation = inject(StringTransformationService);
  protected readonly api = inject(API);
  protected readonly modalNavigation = inject(ModalNavigationService);
  protected readonly util = inject(UtilService);
  protected readonly errorHandler = inject(ErrorService);

  isInModalContext: boolean;
  blockBackdrop?: boolean;

  exportOptions: ExportModalOptions;

  fromDate: SbDateTime;
  toDate: SbDateTime;

  todayStart: SbDateTime;

  loading: boolean = false;

  msDatatypeOptions: MsOptions;
  filteredDatatypes: { id: string }[];
  datatypes: { id: string }[];

  ngOnInit() {
    this.todayStart = new SbDateTime();
    this.todayStart.value = DateTime.utc();
    this.todayStart.value = this.todayStart.value.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    if (this.isInModalContext) {
      if (this.modalNavigation.getCurrentNavigation().data) {
        this.exportOptions = this.modalNavigation.getCurrentNavigation().data;
      }
    }
  }

  handleDateChange($event: { fromDate: SbDateTime; toDate: SbDateTime }): void {
    if ($event) {
      if ($event.fromDate) {
        this.fromDate = $event.fromDate;
      }
      if ($event.toDate) {
        this.toDate = $event.toDate;
      }
    }
  }

  export(): void {
    if (!this.exportOptions?.id || !this.exportOptions?.kind) {
      throw new Error(`No Options provided`);
    }
    let obs: Observable<FileLink>;
    switch (this.exportOptions.kind) {
      case 'DEVICE_LOG':
        const datatypes: string[] = this.filteredDatatypes.map(datatype => datatype.id) ?? [];

        obs = this.api.ss_nm_DeviceLog_exportLogs({
          device: [this.exportOptions.id],
          datatype: datatypes?.length > 0 ? datatypes : undefined,
          from: this.fromDate,
          till: this.toDate,
        });
        break;
      case 'DEVICE_PLAYOUTS':
        obs = this.api.ss_nm_Device_exportPlayouts(this.exportOptions.id, {
          from: this.fromDate,
          till: this.toDate,
        });
        break;
      case 'DESCRIPTOR':
        obs = this.api.ss_cd_Playout_exportCSV({
          descriptor: [this.exportOptions.id],
          from: this.fromDate,
          till: this.toDate,
        });
        break;
      default:
        break;
    }

    if (!obs) {
      return;
    }
    this.loading = true;
    obs
      .pipe(
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data: FileLink) => {
          this.util.downloadFileLink(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  msRequest(options?: MsOptions) {
    if (options) {
      this.msDatatypeOptions = options;
    }
    if (this.msDatatypeOptions && this.msDatatypeOptions.pagination) {
      this.api
        .ss_nm_DeviceLog_datatypes(
          {
            search: this.stringTransformation.encodeSearchQueryParam(false, this.msDatatypeOptions?.search),
          },
          this.msDatatypeOptions.pagination
        )
        .subscribe({
          next: (datatypes: string[]) => {
            this.datatypes = datatypes.map(type => ({ id: type })) ?? [];
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  msUpdate($event) {
    if ($event && $event?.length > 0) {
      this.filteredDatatypes = $event;
    } else {
      this.filteredDatatypes = [];
    }
  }
}
